import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthProvider'
import CreatorAuthGate from '../auth/CreatorAuthGate'
import { resolveInvite, joinOrganization } from '../../lib/organization'
import { ensureSignedIn } from '../../lib/auth'
import { SessionError } from '../../lib/session'
import type { Organization } from '../../types'

export default function JoinOrgPage() {
  const { inviteCode } = useParams<{ inviteCode: string }>()
  const navigate = useNavigate()
  const { user, verified } = useAuth()
  const [org, setOrg] = useState<Organization | null>(null)
  const [loading, setLoading] = useState(true)
  const [joining, setJoining] = useState(false)
  const [error, setError] = useState('')
  const [authComplete, setAuthComplete] = useState(false)

  useEffect(() => {
    if (verified) setAuthComplete(true)
  }, [verified])

  useEffect(() => {
    if (!inviteCode) return
    // An invitee arrives signed out; resolving the invite still needs an identity
    // because org documents are no longer world-readable.
    ensureSignedIn()
      .then(() => resolveInvite(inviteCode))
      .then((result) => setOrg(result ? ({ id: result.orgId, name: result.name } as Organization) : null))
      .catch((err) => {
        console.error('Invite lookup failed:', err)
        setOrg(null)
      })
      .finally(() => setLoading(false))
  }, [inviteCode])

  async function handleJoin() {
    if (!user || !inviteCode) return
    setJoining(true)
    setError('')
    try {
      const result = await joinOrganization(inviteCode, user.displayName || 'Unknown')
      if (result) {
        navigate(`/org/${result.orgId}`)
      } else {
        setError('Invalid invite code.')
      }
    } catch (err) {
      setError(
        err instanceof SessionError && err.code === 'SIGN_IN_REQUIRED'
          ? err.message
          : 'Failed to join team. Please try again.',
      )
    } finally {
      setJoining(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-stone flex items-center justify-center">
        <p className="mono-label text-navy/70">
          Looking up the invite
          <span className="caret" aria-hidden="true" />
        </p>
      </div>
    )
  }

  if (!org) {
    return (
      <div className="min-h-screen bg-stone flex items-center justify-center">
        <div className="text-center space-y-4">
          <h1 className="hl text-2xl text-navy">Invite not found</h1>
          <p className="text-sm text-navy/72">That invite code does not match a team.</p>
          <button onClick={() => navigate('/')} className="text-sm text-amber hover:text-amber-dark font-semibold">
            Back to the homepage
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-stone flex items-center justify-center p-4">
      <div className="card card-shadow w-full max-w-md p-8">
        <h1 className="hl text-2xl text-navy mb-2">Join the team</h1>
        <p className="text-sm text-navy/72 mb-6">
          You've been invited to join <span className="font-semibold text-navy">{org.name}</span>
        </p>

        {!authComplete ? (
          <CreatorAuthGate onReady={() => setAuthComplete(true)} />
        ) : (
          <div className="space-y-4">
            <div className="bg-paper border-2 border-navy p-4">
              <p className="text-xs text-navy/70 mb-1">Joining as</p>
              <p className="text-sm font-semibold text-navy">{user?.displayName || user?.email}</p>
            </div>
            {error && <p className="text-brick text-sm">{error}</p>}
            <button
              onClick={handleJoin}
              disabled={joining}
              className="btn btn-amber w-full"
            >
              {joining ? 'Joining' : 'Join the team'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
